/*
 * How an outbox item leaves the device: one transport per key prefix (`targetKey.ts` `prefixOf`),
 * registered by the module that owns those keys. Chat registers `c` and `e` and sends a socket frame;
 * an AI chat registers `ai` and `aiq` and sends an HTTP request. The outbox itself — durable before
 * the composer clears, one leader per session, FIFO per target, the backoff, the undo window — is
 * the same for both and knows neither wire (S03 §7).
 *
 * A transport resolves when the server has the item and rejects with the server's error otherwise;
 * `dispatch.ts` `classify` reads the rejection (retry, refusal, or a send the author must decide).
 * The item's `clientMsgId` is the idempotency key either way, so a retry after a lost answer is
 * harmless on every wire.
 */

import { prefixOf } from "./targetKey.ts"
import type { OutboxItem, TargetKey } from "./types.ts"

/** Sends one item; resolves once the server has it, rejects with its error otherwise. */
export type OutboxTransport = (item: OutboxItem) => Promise<unknown>

const transports = new Map<string, OutboxTransport>()

/**
 * Registers the transport for every key under `prefix`. A second registration of one prefix is a
 * wiring bug — two modules claiming one key shape — and throws at boot rather than letting one of
 * them silently win. Returns the function that takes the registration back, for a test.
 */
export function registerOutboxTransport(prefix: string, send: OutboxTransport): () => void {
	if (transports.has(prefix)) throw new Error(`outbox transport ${prefix} registered twice`)
	transports.set(prefix, send)
	return () => {
		if (transports.get(prefix) === send) transports.delete(prefix)
	}
}

/**
 * The transport of a key, or undefined while no module has registered its prefix — a module this
 * build or this instance does not run. The outbox then keeps the item and asks again later: the
 * message is the person's, and a missing module is no reason to lose it.
 */
export function transportFor(target: TargetKey): OutboxTransport | undefined {
	return transports.get(prefixOf(target))
}
